// The 5-tab strip. Renders the 5 KNOWN_TABS (FROZEN order) so the shell is
// demoable with ZERO features registered — a registered TabDef enriches its
// tab with a real icon + badge()/live() selectors; an unregistered tab shows a
// neutral glyph. Active = useTabStore.activeTab; onClick → setTab(id).
//
// The reference implementation of CLAUDE.md §9: logical utilities only
// (no ml-/right-), all labels via t(labelKey), the badge anchors with `end-0`
// (not `right-0`), `dir` drives the flex reorder.

import { useTranslation } from 'react-i18next'
import { useTabStore } from '@/ui/stores/use-tab-store'
import { KNOWN_TABS, getTab } from './tab-registry'

export function TabBar(): JSX.Element {
  const { t } = useTranslation()
  const activeTab = useTabStore((s) => s.activeTab)
  const setTab = useTabStore((s) => s.setTab)

  return (
    <div
      role="tablist"
      data-testid="shell-tabbar"
      className="flex border-b border-ink/10 bg-[var(--bg-card)]"
    >
      {KNOWN_TABS.map((known) => {
        const def = getTab(known.id)
        const isActive = activeTab === known.id
        const count = def?.badge ? def.badge() : null
        const isLive = def?.live ? def.live() : false
        return (
          <button
            key={known.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`shell-tab-${known.id}`}
            onClick={() => setTab(known.id)}
            className={[
              'relative flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[10px] font-semibold',
              isActive
                ? 'border-b-2 border-ink text-ink'
                : 'border-b-2 border-transparent text-ink/50 hover:text-ink/80',
            ].join(' ')}
          >
            {isLive && (
              <span
                aria-hidden="true"
                data-testid={`shell-tab-${known.id}-live`}
                className="absolute end-1 top-1 h-1.5 w-1.5 rounded-full bg-gold"
              />
            )}
            <span aria-hidden="true" className="shell-tab-icon text-sm leading-none">
              {def?.icon ?? '•'}
            </span>
            <span>{t(known.labelKey)}</span>
            {count != null && (
              <span
                data-testid={`shell-tab-${known.id}-badge`}
                className="absolute end-1 top-1 min-w-[14px] rounded-full bg-ink px-1 text-[9px] leading-[14px] text-white"
              >
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
